import { Icon } from "@iconify/react";
import {
  Button,
  Card,
  Flex,
  Grid,
  Input,
  message,
  Pagination,
  Table,
  Tag,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { userService } from "../../services/user.service";
import useTitleStore from "../../store/titleStore";
import { useTableHeight } from "../../hooks/useTableHeight";
import useDebounce from "../../hooks/useDebounce";
import TableAction from "../../components/TableAction";
import UpdatedByCell from "../../components/UpdatedByCell";
import { useMe } from "../../hooks/useMe";

export default function UserIndex() {
  const breakpoint = Grid.useBreakpoint();

  const navigate = useNavigate();
  const { setTitle } = useTitleStore();
  const queryClient = useQueryClient();
  const tableHeight = useTableHeight();
  const { data: userDataProfile } = useMe();

  const [messageApi, contextHolder] = message.useMessage();
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    keyword: null,
  });

  const userPosition = userDataProfile?.data?.position;

  const {
    data: userData,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ["users", dataParams],
    queryFn: () => userService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
  });

  const { mutate: handleDelete, isPending: deleteLoading } = useMutation({
    mutationFn: (id) => userService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries("users");
      messageApi.success("Data berhasil dihapus");
    },
    onError: (error) => {
      console.error(error);
      messageApi.error("Terjadi kesalahan saat menghapus data");
    },
  });

  useEffect(() => {
    setTitle("User Management");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams((prev) => ({
      ...prev,
      keyword: debouncedKeyword,
    }));
  }, [debouncedKeyword]);

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page,
    });
  };

  const columns = [
    {
      title: "No",
      align: "center",
      width: 30,
      render: (text, record, index) =>
        (userData?.pagination.page - 1) * dataParams.limit + index + 1,
      fixed: breakpoint.md && "left",
    },
    {
      title: "Nama Lengkap",
      dataIndex: "fullName",
      width: 120,
      fixed: breakpoint.md && "left",
    },
    {
      title: "Jabatan",
      dataIndex: "position",
      width: 100,
      render: (val) => (
        <Typography.Text style={{ fontWeight: 600 }}>{val}</Typography.Text>
      ),
    },
    {
      title: "Provinsi",
      dataIndex: "province",
      width: 180,
      render: (value) => value || "-",
    },
    {
      title: "Alamat Email",
      dataIndex: "email",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 80,
      render: (val) =>
        val ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>,
    },
    {
      title: "Update By",
      width: 120,
      render: (val) => <UpdatedByCell value={val} />,
    },
    {
      className: "last-cell-p",
      title: "Aksi",
      align: "right",
      width: 90,
      fixed: "right",
      render: (record) => {
        const targetPosition = record.position?.toLowerCase();
        const currentUserPosition = userPosition?.toLowerCase();

        const isTargetAdmin =
          targetPosition === "superadmin" || targetPosition === "admin";
        const isCurrentUserAdmin =
          currentUserPosition === "superadmin" ||
          currentUserPosition === "admin" ||
          userPosition === undefined;
        const isCurrentUserVerifikator = currentUserPosition === "verifikator";
        const isCurrentUserDirektur = currentUserPosition === "direktur";

        let canEdit = true;
        let canDelete = false;

        if (isCurrentUserAdmin) {
          canEdit = true;
          canDelete = true;
        } else if (isCurrentUserDirektur) {
          canEdit = !isTargetAdmin;
          canDelete = false;
        } else if (isCurrentUserVerifikator) {
          canEdit = false;
          canDelete = false;
        }

        return (
          <TableAction
            onClickEdit={() => navigate("/user/detail/" + record.uuid)}
            handleDelete={() => handleDelete(record.uuid)}
            showDelete={canDelete}
            showEdit={canEdit}
            deleteLoading={deleteLoading}
          />
        );
      },
    },
  ].filter(Boolean);

  return (
    <Card
      styles={{
        body: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      }}
    >
      {contextHolder}
      <Flex
        justify="space-between"
        gap={20}
        style={{ paddingInline: 24, marginBottom: 24 }}
      >
        <Input
          style={{ width: 320 }}
          prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
          placeholder="Cari"
          onChange={(e) => setKeyword(e.target.value)}
          value={keyword}
        />
        {(userPosition?.toLowerCase() === "superadmin" ||
          userPosition?.toLowerCase() === "admin" ||
          userPosition?.toLowerCase() === "direktur" ||
          userPosition === undefined) && (
          <Button
            variant="solid"
            color="primary"
            icon={<Icon icon="duo-icons:add-circle" width="22" height="22" />}
            onClick={() => navigate("/user/create")}
            style={{ minWidth: 38 }}
          >
            {breakpoint.md && "Tambah User"}
          </Button>
        )}
      </Flex>
      <Table
        columns={columns}
        pagination={false}
        size="small"
        loading={isLoading || isRefetching}
        rowKey={"id"}
        scroll={{ y: tableHeight, x: 1100 }}
        dataSource={userData?.data}
      />
      <Pagination
        style={{ marginInline: 24, marginTop: 16 }}
        total={userData?.pagination.total}
        showSizeChanger={{
          showSearch: false,
        }}
        pageSizeOptions={["10", "20", "50"]}
        current={userData?.pagination.page}
        pageSize={dataParams.limit}
        align="end"
        onChange={handleChangePage}
      />
    </Card>
  );
}
